import type { ImageDto, ProductCard, ProductDetail } from '@sbg/shared/schemas';
import { useCallback, useMemo, useState } from 'react';
import { isApiError } from '@/lib/api/client';
import { useProduct, useProductPages } from '@/lib/catalog';

/** Products per request: three full rows of the picker's grid. */
export const PICKER_PAGE_SIZE = 12;

export interface StoneSelection {
  /** The stone on screen: the one in the URL, else the default. `null` while the default is unknown. */
  slug: string | null;
  /** The URL names a stone the catalogue does not have (so another one was chosen). */
  unknownRequested: boolean;
  detail: ProductDetail | undefined;
  /** The seamless tile to lay on the surfaces (the cover photograph when a product has no tile). */
  image: ImageDto | null;
  /**
   * `loading` while the catalogue is answering · `ready` once the stone is known · `unavailable` when it
   * cannot be reached (the scenes then show the built-in sample stone).
   */
  status: 'loading' | 'ready' | 'unavailable';
}

/**
 * Which stone to show. `?product=` wins; without it — or when it names nothing the catalogue knows —
 * the default is the first product of the unfiltered, featured-first list (the same request the picker
 * makes, so it costs nothing extra). The stone's detail carries the texture.
 */
export function useStoneSelection(requested: string | null): StoneSelection {
  const asked = useProduct(requested ?? undefined);
  const unknown =
    requested !== null && asked.isError && isApiError(asked.error) && asked.error.status === 404;

  const needDefault = requested === null || unknown;
  const list = useProductPages({}, { limit: PICKER_PAGE_SIZE });
  const firstOfList: ProductCard | undefined = list.data?.pages[0]?.items[0];

  const slug = needDefault ? (firstOfList?.slug ?? null) : requested;
  const chosen = useProduct(slug ?? undefined);
  const detail = chosen.data;
  const image = detail ? (detail.texture ?? detail.cover) : null;

  let status: StoneSelection['status'] = 'loading';
  if (detail && image) status = 'ready';
  else if (detail && !image) status = 'unavailable';
  else if (chosen.isError || (needDefault && list.isError)) status = 'unavailable';
  else if (needDefault && !list.isPending && !firstOfList) status = 'unavailable';

  return { slug, unknownRequested: unknown, detail, image, status };
}

export interface ResolvedTexture {
  /** The image id: changes with the stone. */
  key: string;
  /** What to paint with. */
  url: string;
  /** Name of the stone this tile belongs to, so labels follow what is on screen (not what is loading). */
  name: string;
}

export interface TextureState {
  /** The last tile that finished loading (kept while the next one loads, so a swap never blanks). */
  resolved: ResolvedTexture | null;
  /** The tile for `image` could not be loaded. */
  failed: boolean;
  resolve: (url: string) => void;
  fail: () => void;
}

/**
 * Tracks the load of the current stone's tile. `resolve` / `fail` are for the `<TextureProbe>` that is
 * actually loading it — they are bound to the image that was current when the probe was rendered, so a
 * slow answer for a stone the visitor has already left is ignored.
 */
export function useTextureState(
  image: ImageDto | null,
  names: { name: string } | null,
): TextureState {
  const [resolved, setResolved] = useState<ResolvedTexture | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const key = image?.id ?? null;
  const name = names?.name ?? '';

  const resolve = useCallback(
    (url: string) => {
      if (key === null) return;
      setResolved({ key, url, name });
      setFailedKey((previous) => (previous === key ? null : previous));
    },
    [key, name],
  );
  const fail = useCallback(() => {
    if (key !== null) setFailedKey(key);
  }, [key]);

  return useMemo(
    () => ({ resolved, failed: key !== null && failedKey === key, resolve, fail }),
    [resolved, failedKey, key, resolve, fail],
  );
}
